import { Text } from "@mantine/core";
import Link from "next/link";
import { useParams } from "next/navigation";
import { twMerge } from "tailwind-merge";

import { ScreenerBuilderStep } from "@/types/screener-builder";
import { capitalizeFirstLetter } from "@/utils/ui";

interface NavItemProps {
  activeStep: ScreenerBuilderStep;
  disabled?: boolean;
  hideStepNumber?: boolean;
  step: ScreenerBuilderStep;
  label?: string;
}

const STEP_NUMBERS = {
  [ScreenerBuilderStep.Position]: 1,
  [ScreenerBuilderStep.Context]: 2,
  [ScreenerBuilderStep.Locations]: 3,
  [ScreenerBuilderStep.Review]: 4,
};

interface LinkClassNames {
  active: string[];
  default: string[];
}
const getClassesWithActive = (active: boolean, classes: LinkClassNames) =>
  twMerge(classes.default, active && classes.active);

const navItemClassNames = {
  active: ["bg-[#EFEAFA]"],
  default: ["flex", "px-[18px]", "py-[9px]", "rounded-full", "w-full"],
};
const navNumberClassNames = {
  active: ["bg-uw-purple", "text-white"],
  default: [
    "bg-[#EFEAFA]",
    "font-bold",
    "flex",
    "items-center",
    "justify-center",
    "mr-[9px]",
    "py-0.5",
    "rounded-full",
    "text-uw-purple",
    "w-[27px]",
  ],
};
const navTextClassNames = {
  active: ["font-semibold", "text-uw-gray-9"],
  default: ["text-uw-gray-5"],
};

export const ScreenerBuilderNavItem = ({
  activeStep,
  disabled = false,
  hideStepNumber = false,
  step,
  label,
}: NavItemProps) => {
  const { id } = useParams<{ id: string }>();
  const isActive = activeStep === step;

  return (
    <Link
      aria-disabled={disabled}
      className={twMerge([disabled && "cursor-not-allowed", "w-full"])}
      href={`/screener-builder/${id}/${step}`}
      onClick={(e) => {
        if (disabled) {
          e.preventDefault();
        }
      }}
    >
      <li className={getClassesWithActive(isActive, navItemClassNames)}>
        {!hideStepNumber && (
          <Text
            className={getClassesWithActive(isActive, navNumberClassNames)}
            size="xs"
          >
            {STEP_NUMBERS[step]}
          </Text>
        )}
        <Text
          className={getClassesWithActive(isActive, navTextClassNames)}
          size="sm"
        >
          {label || capitalizeFirstLetter(step)}
        </Text>
      </li>
    </Link>
  );
};
